"use client"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import type { ComponentPropsWithRef } from "react"

import { DialogPortal } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

import { useModalInstance } from "./instance-context"

// Chrome shared by every modal: background, corners, the mobile bottom-sheet
// position, the grab bar, and the open/close animation. Two things are
// deliberately absent.
// The overlay stays in ModalShell, which keeps it mounted across the loading
// phase so the backdrop does not re-fade when the content replaces the
// spinner. The close button is rendered by the content, so a modal can place
// it, restyle it or drop it. A feature overrides the rest through className —
// twMerge keeps the caller's utility.
const surfaceClass = [
  "fixed top-[50%] left-[50%] z-1200 grid w-full max-w-lg origin-center",
  "translate-x-[-50%] translate-y-[-50%] overflow-hidden bg-black-800 text-white",
  "rounded-t-[20px] rounded-b-none md:rounded-[10px] md:border md:border-white/10",
  "max-md:top-auto max-md:bottom-0 max-md:max-h-dvh max-md:translate-y-0",
  "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
].join(" ")

export function ModalSurface({
  className,
  children,
  ...props
}: ComponentPropsWithRef<typeof DialogPrimitive.Content>) {
  const { swipe } = useModalInstance()

  return (
    <DialogPortal>
      <DialogPrimitive.Content
        className={cn(surfaceClass, className)}
        {...props}
      >
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 z-20 flex h-8 touch-none items-start justify-center pt-3 md:hidden"
          {...swipe}
        >
          <span className="h-1 w-[100px] rounded-[1000px] bg-white/30" />
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}
